import { IconArrowLeft, IconCheckBold, IconChevRight } from '@assets/images/icon';
import { Icon } from '@pages/Main/Common/Functions/Functions.style';
import React, { useEffect, useState } from 'react';
import {
  FlipHorizontal,
  SchemeMobileButtonLayout,
  SchemeMobileCell,
  SchemeMobileCellRight,
  SchemeMobileLayout,
  SchemeMobilePage,
  SchemeProductSection,
} from './SchemeForMobile.style';
import { DivAllCenter, DivFlexColumn, DivFlexNoRow, DivFlexRow, DivHeight, DivJustify, DivWidth100 } from '@pages/CommonStyle/commonDiv.style';
import { BorderBottomLine, HeadingColor, ImgWH, ImgWidth100, LabelColor, Spacing } from '@pages/CommonStyle/common.style';

import rtlsSchemeKo from '@src/lang/ko/main/rtls/productScheme';
import rtlsSchemeJp from '@src/lang/jp/main/rtls/productScheme';
import rtlsSchemeCn from '@src/lang/cn/main/rtls/productScheme';
import rtlsSchemeEn from '@src/lang/en/main/rtls/productScheme';
import rtlsSchemeVi from '@src/lang/vi/main/rtls/productScheme';

import cameraSchemeKo from '@src/lang/ko/main/camera/productScheme';
import cameraSchemeJp from '@src/lang/jp/main/camera/productScheme';
import cameraSchemeCn from '@src/lang/cn/main/camera/productScheme';
import cameraSchemeEn from '@src/lang/en/main/camera/productScheme';
import cameraSchemeVi from '@src/lang/vi/main/camera/productScheme';

import accessSchemeKo from '@src/lang/ko/main/access/productScheme';
import accessSchemeJp from '@src/lang/jp/main/access/productScheme';
import accessSchemeCn from '@src/lang/cn/main/access/productScheme';
import accessSchemeEn from '@src/lang/en/main/access/productScheme';
import accessSchemeVi from '@src/lang/vi/main/access/productScheme';

import outdoorSchemeKo from '@src/lang/ko/main/outdoor/productScheme';
import outdoorSchemeJp from '@src/lang/jp/main/outdoor/productScheme';
import outdoorSchemeCn from '@src/lang/cn/main/outdoor/productScheme';
import outdoorSchemeEn from '@src/lang/en/main/outdoor/productScheme';
import outdoorSchemeVi from '@src/lang/vi/main/outdoor/productScheme';

import iotSchemeKo from '@src/lang/ko/main/iot/productScheme';
import iotSchemeJp from '@src/lang/jp/main/iot/productScheme';
import iotSchemeCn from '@src/lang/cn/main/iot/productScheme';
import iotSchemeEn from '@src/lang/en/main/iot/productScheme';
import iotSchemeVi from '@src/lang/vi/main/iot/productScheme';

import { Link, useLocation } from 'react-router-dom';
import { Trans } from 'react-i18next';
import { DisplayH2 } from '@components/Text/HeadingTagDisplay.style';
import { DivPaddingRight } from '@pages/Enterprise/Technology/Technology.style';
import { SchemeLinkButton, StyledProductButton, StyledProductLayer } from '@pages/Main/Common/Scheme/Scheme.style';
import i18n from '@pages/i18n';

// Data arrays: a, b, c, d, e imported or defined above
const dataMaps = { b: 'b', c: 'c', d: 'd', e: 'e' };

const getTableSet = (i, data) => {
  const key = dataMaps[Object.keys(dataMaps)[i - 1]];
  const aData = data.a || [];
  const detailData = data[key] || [];

  return (
    <div className="flex flex-col gap-2 mt-2">
      {aData.map((aItem, idx) => (
        <div key={idx} className="flex items-start gap-2">
          <span className="text-sm font-medium text-gray-600 w-28 flex items-center gap-1">
            <aItem.icon size={14} /> {aItem.text}
          </span>
          <span className="text-sm text-gray-800" dangerouslySetInnerHTML={{ __html: detailData[idx]?.text || '' }} />
        </div>
      ))}
    </div>
  );
};

const SchemeForMobile = ({ product }): React.ReactElement => {
  const location = useLocation();

  const language = (() => {
    if (location.pathname.includes('/en/')) return 'en';
    if (location.pathname.includes('/cn/')) return 'cn';
    if (location.pathname.includes('/jp/')) return 'jp';
    if (location.pathname.includes('/vi/')) return 'vi';
    return 'ko';
  })();

  const dataMap = {
    rtls: {
      ko: rtlsSchemeKo,
      en: rtlsSchemeEn,
      cn: rtlsSchemeCn,
      jp: rtlsSchemeJp,
      vi: rtlsSchemeVi,
    },
    camera: {
      ko: cameraSchemeKo,
      en: cameraSchemeEn,
      cn: cameraSchemeCn,
      jp: cameraSchemeJp,
      vi: cameraSchemeVi,
    },
    access: {
      ko: accessSchemeKo,
      en: accessSchemeEn,
      cn: accessSchemeCn,
      jp: accessSchemeJp,
      vi: accessSchemeVi,
    },
    outdoor: {
      ko: outdoorSchemeKo,
      en: outdoorSchemeEn,
      cn: outdoorSchemeCn,
      jp: outdoorSchemeJp,
      vi: outdoorSchemeVi,
    },
    sensor: {
      ko: iotSchemeKo,
      en: iotSchemeEn,
      cn: iotSchemeCn,
      jp: iotSchemeJp,
      vi: iotSchemeVi,
    },
  };

  const [dataSheet, setDataSheet] = useState<any>(dataMap[product]?.[language] || rtlsSchemeKo);
  const [infoSheet, setInfoSheet] = useState<any>(dataSheet.b);

  useEffect(() => {
    const selected = dataMap[product]?.[language] || rtlsSchemeKo;
    setDataSheet(selected);
  }, [location.pathname, product]);

  const [index, setIndex] = useState(1);

  // 총 유효 인덱스 범위: 1 ~ (dataSheet.spec.length - 1)
  const next = () => setIndex((prev) => (prev === dataSheet.spec.length - 1 ? 1 : prev + 1));

  const prev = () => setIndex((prev) => (prev === 1 ? dataSheet.spec.length - 1 : prev - 1));
  useEffect(() => {
    if (index == 1) {
      setInfoSheet(dataSheet.b);
    } else if (index == 2) {
      setInfoSheet(dataSheet.c);
    } else if (index == 3) {
      setInfoSheet(dataSheet.d);
    } else if (index == 4) {
      setInfoSheet(dataSheet.e);
    }
  }, [index, dataSheet]);

  return (
    <SchemeMobilePage>
      <Spacing pixel="96" />
      <SchemeProductSection>
        <DivAllCenter>
          <DisplayH2 size={'small'}>
            <Trans i18nKey={`rtls.banner.schemeTitle`} />
          </DisplayH2>
          <Spacing pixel="32" />
          <ImgWH w="100%" h="160" src={dataSheet.spec[index].image} />
          <HeadingColor color="#2A70F0" size="xxxsmall">
            <Trans i18nKey={dataSheet.spec[index].title} />
          </HeadingColor>
          <Spacing pixel="4" />
          <LabelColor color="#818181" size="small">
            <Trans i18nKey={dataSheet.spec[index].desc} />
          </LabelColor>
          <Spacing pixel="32" />
        </DivAllCenter>
        {dataSheet.a.map((data, i) => (
          <>
            <SchemeMobileCell>
              <DivFlexNoRow>
                <ImgWH w="16" h="16" src={data.icon} />
                <DivPaddingRight pixel="10" />
                <LabelColor color="#000" size="small">
                  <Trans i18nKey={data.text} />
                </LabelColor>
              </DivFlexNoRow>
              <SchemeMobileCellRight>
                {infoSheet[i].text === '1' ? (
                  <>
                    <ImgWH w="12" h="12" src={IconCheckBold} alt="Check Icon" />
                  </>
                ) : (
                  <>
                    <LabelColor color="#818181" size="small">
                      <Trans i18nKey={infoSheet[i].text} />
                    </LabelColor>
                  </>
                )}
              </SchemeMobileCellRight>
            </SchemeMobileCell>
            {i < dataSheet.a.length - 1 && <BorderBottomLine />}
          </>
        ))}

        <StyledProductLayer>
          <SchemeLinkButton>
            <>
              <Link to={i18n.language === 'ko' ? dataSheet.spec[index].link : `/${i18n.language}${dataSheet.spec[index].link}`}>
                <StyledProductButton>
                  <Trans i18nKey={'home.showMore'} />
                </StyledProductButton>
              </Link>
            </>
          </SchemeLinkButton>
        </StyledProductLayer>

        <SchemeMobileButtonLayout onClick={prev} position="left">
          <ImgWH w="24" h="24" src={IconArrowLeft} />
        </SchemeMobileButtonLayout>
        <SchemeMobileButtonLayout onClick={next} position="right">
          <FlipHorizontal>
            <ImgWH w="24" h="24" src={IconArrowLeft} />
          </FlipHorizontal>
        </SchemeMobileButtonLayout>
      </SchemeProductSection>
    </SchemeMobilePage>
  );
};

export default SchemeForMobile;
